import React, { FC, useEffect, useMemo, useState } from "react";
import { LuChevronDown, LuChevronRight, LuUsers, LuX } from "react-icons/lu";

import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../../slices/ContactsSlice";
import {
  useGetSeasonalQuery,
  useSetSeasonalMutation,
} from "../../../slices/SubscriptionSlice";
import {
  useEditUserMutation,
  useGetPermissionsQuery,
  useGetUserRoleEnumQuery,
} from "../../../slices/UserSlice";
import { IUser } from "../../../types/types";
import { seasonalEditState } from "../../subscription/subscription.utils";
import { useSubscriptionStatus } from "../../subscription/useSubscription";
import {
  AssignClientsModal,
  AssignedClient,
  toAssignedClient,
} from "./AssignClientsModal";
import {
  ACCESS_LEVELS,
  ADMIN_PERMISSIONS_NOTE,
  ColorPicker,
  DEFAULT_AVATAR_COLOR,
  InfoTip,
  isAdminLevel,
  isIndependentPermission,
  matchAccessLevel,
  permissionHelp,
  presetPermissions,
  sortPermissions,
  Toggle,
  UserAvatar,
  userFullName,
  visiblePermissions,
  withIndependentSwitches,
  withoutHiddenPermissions,
} from "./editUserParts";
import {
  ADMIN_PERMISSIONS_NOTE_SEATS,
  MANAGE_SWITCH_LOCKED_NOTE,
} from "./teamBilling";

type Props = {
  user: IUser;
  teammates: IUser[];
  onClose: () => void;
};

const LABEL_CLASSES = "mb-2 block text-sm font-bold text-[#293B5A]";

export const EditUserModal: FC<Props> = ({ user, teammates, onClose }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: allPermissions } = useGetPermissionsQuery();
  // "Manage subscription & payment" shows only for firms billed per user.
  const { status: billing } = useSubscriptionStatus(orgId);
  const permissions = useMemo(
    () =>
      visiblePermissions(allPermissions, {
        seatBilling: !!billing?.seatBilling,
      }),
    [allPermissions, billing?.seatBilling],
  );
  const { data: userRoleEnum } = useGetUserRoleEnumQuery();
  const { data: customers } = useGetSearchCustomersQuery({
    orgId,
    searchQuery: "",
    userId: user.id,
    isActive: true,
  });
  const [editUser, { isLoading: isSaving, isError }] = useEditUserMutation();
  // Free seasonal users (Feb 1 – Apr 30): saved with Update through api/subscription/seasonal.
  const { data: seasonalInfo } = useGetSeasonalQuery(
    { orgId },
    { skip: !orgId || !billing?.seatBilling, refetchOnMountOrArgChange: true },
  );
  const seasonalState = seasonalEditState(
    seasonalInfo,
    user.id,
    !!billing?.billingContactName &&
      billing.billingContactName.trim() === userFullName(user),
  );
  const [seasonalChoice, setSeasonalChoice] = useState<boolean | null>(null);
  const seasonal = seasonalChoice ?? seasonalState.isSeasonal;
  const [setSeasonalOnServer, { isLoading: isSavingSeasonal }] =
    useSetSeasonalMutation();
  const [seasonalError, setSeasonalError] = useState("");

  const [accessLevel, setAccessLevel] = useState<string>(
    user.userRoleEnum?.userRole || "Custom",
  );
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    withoutHiddenPermissions(user.permissionIds),
  );
  const [rate, setRate] = useState(Number(user.hourlyRate ?? 0).toFixed(2));
  const [avatarColor, setAvatarColor] = useState(
    user.userProfile?.color || DEFAULT_AVATAR_COLOR,
  );
  const [assignedAll, setAssignedAll] = useState(!!user.assignedAllClients);
  // null = untouched: keep exactly what the user already has.
  const [assignedClients, setAssignedClients] = useState<
    AssignedClient[] | null
  >(null);
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  // Use the server's list of access levels (Custom last) once it has loaded.
  const accessLevels = useMemo(
    () =>
      userRoleEnum?.length
        ? [...userRoleEnum]
            .sort(
              (a, b) =>
                Number(a.userRole === "Custom") -
                Number(b.userRole === "Custom"),
            )
            .map((item) => item.userRole)
        : [...ACCESS_LEVELS],
    [userRoleEnum],
  );

  const allPermissionIds = useMemo(
    () => (permissions ?? []).map((permission) => permission.id),
    [permissions],
  );
  const allClients = useMemo(
    () => (customers ?? []).map(toAssignedClient),
    [customers],
  );
  const currentClients = useMemo(
    () =>
      assignedClients ??
      allClients.filter((client) =>
        (user.assignedCustomerIds ?? []).includes(client.id),
      ),
    [assignedClients, allClients, user.assignedCustomerIds],
  );
  const takenColors = useMemo(
    () =>
      teammates
        .filter((teammate) => teammate.id !== user.id)
        .map((teammate) => teammate.userProfile?.color)
        .filter(Boolean) as string[],
    [teammates, user.id],
  );

  useEffect(() => {
    document.title = "Edit user | ProCharted";
  }, []);

  const changeAccessLevel = (level: string) => {
    setAccessLevel(level);
    const preset = presetPermissions(level, allPermissionIds);
    if (preset)
      setSelectedPermissions(
        withIndependentSwitches(preset, selectedPermissions),
      );
  };

  const togglePermission = (id: string, checked: boolean) => {
    const next = checked
      ? [...selectedPermissions, id]
      : selectedPermissions.filter((permissionId) => permissionId !== id);
    setSelectedPermissions(next);
    setAccessLevel(matchAccessLevel(next, allPermissionIds));
  };

  const handleSave = async () => {
    const hourlyRate = Number(rate);
    try {
      await editUser({
        ...user,
        userRoleEnumId:
          userRoleEnum?.find((item) => item.userRole === accessLevel)?.id ||
          user.userRoleEnumId,
        permissionIds: selectedPermissions,
        assignedAllClients: assignedAll,
        assignedCustomerIds: assignedAll
          ? []
          : assignedClients
          ? assignedClients.map((client) => client.id)
          : user.assignedCustomerIds ?? [],
        hourlyRate: Number.isFinite(hourlyRate) ? hourlyRate : 0,
        color: avatarColor,
      }).unwrap();
    } catch {
      // The error message is shown below.
      return;
    }
    if (seasonalState.show && seasonal !== seasonalState.isSeasonal) {
      try {
        await setSeasonalOnServer({
          orgId,
          userId: user.id,
          seasonal,
        }).unwrap();
      } catch (error) {
        setSeasonalError(
          typeof error === "string"
            ? error
            : "Couldn't change the seasonal setting. Please try again.",
        );
        return;
      }
    }
    onClose();
  };

  const clientsSummary = assignedAll
    ? "All clients"
    : currentClients.length
    ? `${currentClients.length} client${
        currentClients.length === 1 ? "" : "s"
      } assigned`
    : "No clients assigned yet";

  return (
    <div className={"modal modal-open font-Manrope"}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Edit user"}
        className={
          "modal-box flex max-h-[calc(100vh-48px)] w-[500px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[14px] p-0 shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }>
        <div
          className={
            "relative flex items-center justify-center border-b border-[#EDEFF3] px-6 py-5"
          }>
          <div className={"text-[17px] font-extrabold text-[#1F2433]"}>
            Edit user
          </div>
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onClose}
            className={
              "absolute right-6 text-[#B4B9C6] transition-colors hover:text-[#1F2433]"
            }>
            <LuX size={18} />
          </button>
        </div>

        <div className={"min-h-0 flex-1 overflow-y-auto px-6 py-5"}>
          <div
            className={
              "mb-5 flex items-center gap-3.5 rounded-xl border border-[#EDEFF3] bg-[#F8F9FB] px-4 py-3.5"
            }>
            <UserAvatar user={user} color={avatarColor} />
            <div className={"min-w-0 flex-1"}>
              <div
                className={
                  "truncate text-[15.5px] font-extrabold text-[#1F2433]"
                }>
                {userFullName(user)}
              </div>
              <div className={"truncate text-[12.5px] text-[#8A90A2]"}>
                {user.userProfile?.email}
              </div>
            </div>
            <ColorPicker
              value={avatarColor}
              takenColors={takenColors}
              onChange={setAvatarColor}
            />
          </div>

          <label
            htmlFor={"access-level"}
            className={
              "mb-2 flex items-center gap-1.5 text-sm font-bold text-[#293B5A]"
            }>
            Access level
            <InfoTip
              tone={"purple"}
              text={
                "A preset bundle of permissions. Pick one to fill the toggles below, or adjust any toggle for a custom set."
              }
            />
          </label>
          <div className={"relative mb-5"}>
            <select
              id={"access-level"}
              value={accessLevel}
              onChange={(event) => changeAccessLevel(event.target.value)}
              className={
                "h-[41px] w-full cursor-pointer appearance-none rounded-[9px] border border-[#DCE2EA] bg-white pl-3.5 pr-9 text-sm text-[#1F2433] outline-none focus:border-[#6B4EFF]"
              }>
              {accessLevels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
            <LuChevronDown
              size={16}
              className={
                "pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[#6B7185]"
              }
            />
          </div>

          <label htmlFor={"hourly-rate"} className={LABEL_CLASSES}>
            Hourly rate
          </label>
          <div
            className={
              "mb-5 flex h-[43px] items-stretch overflow-hidden rounded-[9px] border border-[#DCE2EA] bg-white focus-within:border-[#6B4EFF]"
            }>
            <span
              className={
                "flex w-10 items-center justify-center border-r border-[#DCE2EA] bg-[#F3F0FF] text-base font-extrabold text-[#6B4EFF]"
              }>
              $
            </span>
            <input
              id={"hourly-rate"}
              type={"text"}
              inputMode={"decimal"}
              value={rate}
              onChange={(event) =>
                setRate(event.target.value.replace(/[^0-9.]/g, ""))
              }
              onBlur={() =>
                setRate((value) =>
                  value === "" || Number.isNaN(Number(value))
                    ? "0.00"
                    : Number(value).toFixed(2),
                )
              }
              className={
                "min-w-0 flex-1 px-3.5 text-sm text-[#1F2433] outline-none"
              }
            />
            <span
              className={
                "flex items-center pr-3.5 text-[13px] font-semibold text-[#9097A8]"
              }>
              / hour
            </span>
          </div>

          {seasonalState.show && (
            <div
              className={
                "mb-5 flex items-center gap-3 rounded-xl border border-[#EDEFF3] bg-[#F8F9FB] px-4 py-3"
              }>
              <div className={"min-w-0 flex-1"}>
                <div className={"text-[13.5px] font-semibold text-[#1F2433]"}>
                  Seasonal user (free)
                </div>
                <div className={"text-[12px] text-[#7A7F8C]"}>
                  {seasonalState.note}
                </div>
              </div>
              <Toggle
                checked={seasonal}
                label={"Seasonal user (free)"}
                disabled={seasonalState.disabled && !seasonal}
                onChange={(checked) => {
                  setSeasonalError("");
                  setSeasonalChoice(checked);
                }}
              />
            </div>
          )}

          <div className={LABEL_CLASSES}>Permissions</div>
          <div
            className={
              "mb-5 rounded-xl border border-[#EDEFF3] bg-[#F8F9FB] px-4"
            }>
            {isAdminLevel(accessLevel) && (
              <div
                className={
                  "border-b border-[#ECEFF3] py-3 text-[12.5px] font-medium text-[#6B7185]"
                }>
                {billing?.seatBilling
                  ? ADMIN_PERMISSIONS_NOTE_SEATS
                  : ADMIN_PERMISSIONS_NOTE}
              </div>
            )}
            {sortPermissions(permissions ?? []).map((permission) => {
              const help = permissionHelp(permission);
              // Independent switches (Manage subscription & payment) stay
              // editable for Admins, but only by people who manage billing.
              const independent = isIndependentPermission(permission.id);
              const isAdmin = isAdminLevel(accessLevel) && !independent;
              const isLocked = independent && !billing?.canManage;
              const isOn =
                isAdmin || selectedPermissions.includes(permission.id);
              return (
                <div
                  key={permission.id}
                  className={
                    "flex items-center justify-between gap-3 border-b border-[#ECEFF3] py-3 last:border-b-0"
                  }>
                  <span
                    className={
                      "flex items-center gap-2 text-[13.5px] font-semibold text-[#1F2433]"
                    }>
                    {permission.permission}
                    {help && <InfoTip text={help} />}
                  </span>
                  <Toggle
                    checked={isOn}
                    label={permission.permission}
                    disabled={isAdmin || isLocked}
                    title={
                      isAdmin
                        ? ADMIN_PERMISSIONS_NOTE
                        : isLocked
                        ? MANAGE_SWITCH_LOCKED_NOTE
                        : undefined
                    }
                    onChange={(checked) =>
                      togglePermission(permission.id, checked)
                    }
                  />
                </div>
              );
            })}
            {!permissions && (
              <div className={"py-4 text-center text-[13px] text-[#8A90A2]"}>
                Loading permissions...
              </div>
            )}
          </div>

          <button
            type={"button"}
            onClick={() => setIsAssignOpen(true)}
            className={
              "flex w-full items-center gap-3.5 rounded-xl border border-[#EDEFF3] bg-white px-4 py-3.5 text-left transition-colors hover:border-[#DDD5FA] hover:bg-[#FAF8FF]"
            }>
            <span
              className={
                "flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#F1F2F5] text-[#5B6478]"
              }>
              <LuUsers size={18} />
            </span>
            <span className={"min-w-0 flex-1"}>
              <span className={"block text-sm font-bold text-[#1F2433]"}>
                Assigned clients
              </span>
              <span className={"block text-[12.5px] text-[#7A7F8C]"}>
                {clientsSummary}
              </span>
            </span>
            <LuChevronRight size={18} className={"text-[#8A90A2]"} />
          </button>

          {isError && (
            <div
              role={"alert"}
              className={
                "mt-4 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
              }>
              Couldn&apos;t save the changes. Please try again.
            </div>
          )}
          {seasonalError && (
            <div
              role={"alert"}
              className={
                "mt-4 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
              }>
              {seasonalError}
            </div>
          )}
        </div>

        <div
          className={
            "flex flex-none items-center justify-end gap-2.5 border-t border-[#EDEFF3] px-6 py-4"
          }>
          <button
            type={"button"}
            onClick={onClose}
            disabled={isSaving || isSavingSeasonal}
            className={
              "h-[38px] rounded-lg border border-[#DCE2EA] bg-white px-[22px] text-sm font-bold text-[#3A4256] hover:bg-[#F8F9FB] disabled:cursor-not-allowed"
            }>
            Cancel
          </button>
          <button
            type={"button"}
            onClick={() => void handleSave()}
            disabled={isSaving || isSavingSeasonal}
            className={
              "h-[38px] rounded-lg bg-[#6B4EFF] px-6 text-sm font-bold text-white shadow-[0_4px_14px_rgba(107,78,255,0.28)] hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:bg-[#C3C8D4] disabled:shadow-none"
            }>
            {isSaving || isSavingSeasonal ? "Saving..." : "Update"}
          </button>
        </div>
      </div>

      {isAssignOpen && (
        <AssignClientsModal
          user={user}
          avatarColor={avatarColor}
          allClients={allClients}
          assignedAll={assignedAll}
          assignedClients={currentClients}
          onClose={() => setIsAssignOpen(false)}
          onSave={(all, clients) => {
            setAssignedAll(all);
            setAssignedClients(clients);
            setIsAssignOpen(false);
          }}
        />
      )}
    </div>
  );
};
